import actions from './actions';
import axios from 'axios';
import { storeAuthData, checkToken } from '../../utilities/authUtilities';
import AuthDialogConstants from '../../components/dialogs/AuthDialog/AuthDialogConstants';
import ColoredSnackbarConstants from '../../components/misc/ColoredSnackbar/ColoredSnackbarConstants';
import { identify, trackEvent, AnalyticsConstants } from '../../utilities/analyticsUtilities';
import { saveStorageItem, EXPIRATION_ONBOARDING_INFO_CLOSED_KEY } from '../../utilities/storageUtilities';
import { createActions } from '../../components/pages/CreatePage/store';
import { executeCarefully } from '../../utilities/basicUtilities';
import { createObjectCopy } from '../../utilities/objectUtilities';

const { 
    setProperty,
    setUpSignalR,
    openSnack,
    closeSnack,
    openAuthDialog,
    closeAuthDialog: closeAuthDialogAction,
    setAuthDialogFormType,
    startAuthDialogRequest,
    stopAuthDialogRequest,
    completeLogin,
    completeSignUp,
    completePasswordRecoverRequest,
    setRefreshMethod,
    setAccountDetails,
    setGettingAccountDetails,
    showInformationDialog
} = actions;

const makeLoggedIn = (token) => {
    return dispatch => {
        dispatch(completeLogin(true));
        dispatch(getAccountDetails(true));
        dispatch(setUpSignalR(token));
    }
}
const login = (email, password, refreshMethod) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());

        axios.post('api/auth/authenticate', {
            email: email,
            password: password,
        }).then(response => {
            storeAuthData(response.data.token, response.data.expiresIn);
            dispatch(completeLogin(true));
            dispatch(getAccountDetails(true));

            if (typeof refreshMethod === "function")
                dispatch(refreshMethod());

            dispatch(setUpSignalR(response.data.token));

            // Updated the presets because the order is different for authenticated and anonymous users
            dispatch(createActions.getPresets());
        }).catch(() => {
            dispatch(completeLogin(false, "Invalid email or password."));
        });
    }
}
const signUp = (email, password, agreeTerms, allowEmails, turnstileAccessToken, refreshMethod) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());
        axios.post('api/account/Register', {
            email: email,
            password: password,
            agreeTerms: agreeTerms,
            allowEmails: allowEmails,
            turnstileAccessToken: turnstileAccessToken
        }).then(response => {
            saveStorageItem(EXPIRATION_ONBOARDING_INFO_CLOSED_KEY, true);
            
            storeAuthData(response.data.token, response.data.expiresIn);
            dispatch(completeSignUp(true));
            dispatch(getAccountDetails(true));
            dispatch(closeAuthDialog());

            if (typeof refreshMethod === "function") 
                dispatch(refreshMethod());

            dispatch(setUpSignalR(response.data.token));

            // Updated the presets because the order is different for authenticated and anonymous users
            dispatch(createActions.getPresets());
        }).catch(error => {
            dispatch(completeSignUp(false, error.response.data));
        });
    }
}
const recoverPassword = (email, callbacks) => {
    return dispatch => {
        const dialogDriven = !callbacks;

        if (dialogDriven)
            dispatch(startAuthDialogRequest());

        axios.post('api/account/Recover', {
            email: email,
        }).then(response => {
            if (dialogDriven)
                dispatch(completePasswordRecoverRequest(true));
            else {
                dispatch(openSnack('A reset link was sent to your email.', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
                callbacks.onSuccess?.();
            }

            trackEvent('Requested Password Reset', {
                email: email,
                category: AnalyticsConstants.ACCOUNT_CATEGORY,
            });
        }).catch(error => {
            if (dialogDriven)
                dispatch(completePasswordRecoverRequest(false, error.response.data));
            else {
                dispatch(openSnack(error.response?.data || 'Failed to send the reset email.', ColoredSnackbarConstants.SNACKBAR_ERROR));
                callbacks.onError?.();
            }
        });
    }
}
const resetPassword = (email, password, code, onInvalidToken) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());
        axios.post('api/account/ResetPassword', {
            email: email,
            password: password,
            code: code
        }).then(response => {
            dispatch(openSnack('Password succesfully reset!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));

            trackEvent('Reset Password', {
                category: AnalyticsConstants.ACCOUNT_CATEGORY,
            });
        }).catch(error => {
            const isInvalidTokenError = error.response?.status === 400
                && typeof error.response.data === 'string'
                && error.response.data.includes('Invalid token');

            if (isInvalidTokenError && typeof onInvalidToken === 'function')
                onInvalidToken();
            else
                dispatch(failRequest(error, resetPassword(email, password, code, onInvalidToken), error.response?.data));
        }).finally(() => {
            dispatch(stopAuthDialogRequest());
        });
    }
}
const changePassword = (password, newPassword) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());
        axios.post('api/account/ChangePassword', {
            currentPassword: password,
            newPassword: newPassword
        }).then(response => {
            dispatch(openSnack('Password succesfully changed!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));

            trackEvent('Changed Password', {
                category: AnalyticsConstants.ACCOUNT_CATEGORY,
            });
        }).catch(error => {
            dispatch(failRequest(error, changePassword(password, newPassword), error.response.data));
        }).finally(() => {
            dispatch(stopAuthDialogRequest());
        });
    }
}
const changeEmail = (password, newEmail, token, onSuccess) => {
    return (dispatch, getState) => {
        const state = getState();

        axios.post('api/account/ChangeEmail', {
            newEmail: newEmail,
            password: password,
            turnstileAccessToken: token
        }).then(() => {
            const accountDetails = state.app.accountDetails;
            const detailsCopy = createObjectCopy(accountDetails);
            detailsCopy.email = newEmail;
            dispatch(setAccountDetails(detailsCopy));
 
            if (onSuccess && typeof onSuccess === "function")
                onSuccess();

            dispatch(openSnack('Email succesfully changed!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));

            trackEvent('Changed Email', {
                category: AnalyticsConstants.ACCOUNT_CATEGORY,
            });
        }).catch(error => {
            if (error?.response?.data) {
                dispatch(failRequest(error, changePassword(password, newEmail, onSuccess), error.response.data));
            }
        })
    }
}
const sendSupportMessage = (message, callback) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());
        axios.post('api/account/SendSupportMessage', { message }).then(() => {
            executeCarefully(callback);
            dispatch(openSnack('Your support request has been submitted.', ColoredSnackbarConstants.SNACKBAR_SUCCESS));

            trackEvent('Submitted Support Request', {
                category: AnalyticsConstants.ACCOUNT_CATEGORY,
            });
        }).catch(error => {
            dispatch(failRequest(error, null, "Could not submit support request. Contact help@specterr.com."));
        }).finally(() => {
            dispatch(stopAuthDialogRequest());
        });
    }
}
const unsubscribeEmail = (email) => {
    return dispatch => {
        dispatch(startAuthDialogRequest());
        axios.post(`api/account/UnsubscribeEmail/${email}`).then(response => {
            dispatch(openSnack('Succesfully unsubscribed!', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        }).catch(error => {
            dispatch(failRequest(error, unsubscribeEmail(email), error.response.data));
        }).finally(() => {
            dispatch(stopAuthDialogRequest());
        });
    }
}
const failRequest = (error, refreshMethod, errorHandlerOrMessage) => {
    return dispatch => {
        if (error && error.response && error.response.status === 401) {
            checkToken(
                false,
                (token) => dispatch(makeLoggedIn(token)),
                () => dispatch(openAuthDialog(AuthDialogConstants.FORM_TYPE_LOGIN)),
                () => dispatch(openAuthDialog(AuthDialogConstants.FORM_TYPE_SIGN_UP)),
            )
            dispatch(setRefreshMethod(refreshMethod));
        } else {
            if (typeof errorHandlerOrMessage === "function")
                errorHandlerOrMessage(error);
            else
                dispatch(openSnack(errorHandlerOrMessage, ColoredSnackbarConstants.SNACKBAR_ERROR));
        }
    }
}

const getAccountDetails = (authenticated = false, completion = null) => {
    return async (dispatch) => {
        try {
            dispatch(setGettingAccountDetails(true));

            const response = await axios.get(`api/account/GetAccountDetails/${authenticated}`);
            dispatch(setAccountDetails(response.data));
            identify(response.data);

            if (response.data.currency !== undefined && response.data.currency !== null) {
                dispatch(setProperty('recommendedCurrency', response.data.currency));
            }

            if (typeof completion === "function") completion();
        } catch (error) {
            dispatch(failRequest(error, null, 'Failed to load account details.'));
        } finally {
            dispatch(setGettingAccountDetails(false));
        }
    }
}

const closeAuthDialog = () => {
    return dispatch => {
        dispatch(closeAuthDialogAction());
    }
}

const loadPurchaseOptions = () => {
    return (dispatch, getState) => {
        const state = getState();
        const purchaseOptions = state.app.purchaseOptions;

        if (Boolean(purchaseOptions.length))
            return;

        dispatch(setProperty('loadingPurchaseOptions', true));

        axios.get('/api/plan/GetPurchaseOptions/').then(response => {
            dispatch(setProperty('purchaseOptions', response.data));
        }).catch(error => {
            dispatch(failRequest(error, () => loadPurchaseOptions(), 'Failed to load purchase options.'));
        }).finally(() => {
            dispatch(setProperty('loadingPurchaseOptions', false));
        });
    }
}

export default {
    setProperty,
    openSnack,
    closeSnack,
    openAuthDialog,
    closeAuthDialog,
    setAuthDialogFormType,
    makeLoggedIn,
    login,
    signUp,
    sendSupportMessage,
    recoverPassword,
    resetPassword,
    changePassword,
    changeEmail,
    unsubscribeEmail,
    failRequest,
    getAccountDetails,
    setAccountDetails,
    loadPurchaseOptions,
    showInformationDialog
}